import type { EarlyAccessFeature } from "@rosc/contracts";
import { BetaBadge, BetaFeedback } from "@rosc/ui/v32/beta";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "@/hooks/use-toast";
import { useIsBeta } from "@/lib/early-access";
import {
  FEEDBACK_MESSAGE_MAX,
  feedbackSendError,
  sendFeedbackReport,
} from "@/lib/in-app-feedback";
import { getVisitScope } from "@/lib/visit-queries";

// Early access in the app: the "Beta" pill and "Send feedback on this beta"
// for a feature this account sees only through early access. Both render
// nothing otherwise (the build flag is on, or no grant).

export const BetaMark = ({
  feature,
  className,
}: {
  feature: EarlyAccessFeature;
  className?: string;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const beta = useIsBeta(feature);
  if (!beta) return null;
  return (
    <BetaBadge
      className={className}
      label={t("beta.badge")}
      testId={`beta-badge-${feature}`}
      title={t("beta.badgeHint")}
    />
  );
};

export const BetaFeedbackButton = ({
  feature,
  appearance,
  className,
}: {
  feature: EarlyAccessFeature;
  appearance?: "button" | "link";
  className?: string;
}): JSX.Element | null => {
  const { t } = useTranslation();
  const beta = useIsBeta(feature);
  if (!beta) return null;
  return (
    <BetaFeedback
      appearance={appearance}
      className={className}
      describeError={(error) =>
        t(`feedback.inApp.errors.${feedbackSendError(error)}`)
      }
      labels={{
        trigger: t("beta.trigger"),
        title: t("beta.title"),
        description: t("beta.description"),
        field: t("beta.field"),
        placeholder: t("beta.placeholder"),
        submit: t("beta.send"),
        cancel: t("beta.cancel"),
        close: t("beta.close"),
        failed: t("feedback.inApp.errors.failed"),
      }}
      maxLength={FEEDBACK_MESSAGE_MAX}
      testId={`beta-feedback-${feature}`}
      onSubmit={async (message) => {
        const scope = getVisitScope();
        if (!scope) throw Error("Authentication required");
        await sendFeedbackReport(
          { type: "other", message, featureKey: feature },
          scope.uid,
        );
        toast({ description: t("beta.sent") });
      }}
    />
  );
};
